import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { useLanguage, useT } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { LabelStatus } from '@/api/types';
import { downloadBase64 } from '@/pages/Imports/skuImportMessages';

/**
 * FBS EXECUTION epic, Part B2 — request/print a package's shipping label
 * directly from this screen. Self-contained so a failed request/print
 * never breaks the rest of the (already-loaded) shipment view — errors
 * render inline, next to the button that caused them, not as a full-page
 * error banner.
 */
function PackageLabelCell({ packageId }: { packageId: string }): JSX.Element {
  const t = useT();
  const [status, setStatus] = useState<LabelStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    endpoints
      .labelStatus(packageId)
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [packageId]);

  async function request(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await endpoints.requestLabel(packageId);
      setStatus(await endpoints.labelStatus(packageId));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('shipmentDetail.requestFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function print(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await endpoints.printLabel(packageId);
      setStatus(await endpoints.labelStatus(packageId));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('shipmentDetail.printFailed'));
    } finally {
      setBusy(false);
    }
  }

  /** The warehouse's own package label: a read, the same file every time, never a second package. */
  async function printPackageLabel(layout: 'LABEL_100x60' | 'A4_8UP'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await endpoints.packageLabel(packageId, layout);
      downloadBase64(r.filename, r.bytesBase64, 'application/pdf');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('shipmentDetail.printFailed'));
    } finally {
      setBusy(false);
    }
  }

  const transportSupported = status?.transportLabelSupported !== false;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="primary" disabled={busy} onClick={() => void printPackageLabel('LABEL_100x60')}>
          {t('shipmentDetail.printPackageLabel')}
        </button>
        <button type="button" disabled={busy} onClick={() => void printPackageLabel('A4_8UP')}>
          {t('shipmentDetail.printPackageLabelA4')}
        </button>
      </div>
      {status && !transportSupported && <span style={{ fontSize: 11 }}>{t('shipmentDetail.noTransportLabel')}</span>}
      {status && transportSupported && (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {status.labelStatus === 'NONE' && (
          <button type="button" disabled={busy} onClick={request}>
            {t('shipmentDetail.requestLabel')}
          </button>
        )}
        {(status.labelStatus === 'READY' || status.printRequestStatus === 'FAILED') && (
          <button type="button" disabled={busy} onClick={print}>
            {status.printRequestStatus === 'FAILED' ? t('shipmentDetail.retryPrint') : t('shipmentDetail.print')}
          </button>
        )}
        {status.labelStatus === 'PRINTED' && <StatusBadge value="SHIPPED" />}
        {status.printRequestStatus && <span style={{ fontSize: 11 }}>{statusLabel(t, status.printRequestStatus)}</span>}
      </div>
      )}
      {error && (
        <span style={{ fontSize: 11, color: 'var(--danger, #b00)' }} role="alert">
          {error}
        </span>
      )}
      {status?.printErrorCode && (
        <span style={{ fontSize: 11, color: 'var(--danger, #b00)' }}>{t('shipmentDetail.lastError', { code: status.printErrorCode })}</span>
      )}
    </div>
  );
}

/** Linked order, packages, package contents, movements, and the explicit custody-exit moment (Part B). */
export function ShipmentDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.shipment(id!), [id]);
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { shipment, packages, contents, exitMovements } = result.data;
  const header = shipment as Record<string, unknown>;

  return (
    <div>
      <div className="page-header">
        <h1>{t('shipmentDetail.title', { number: String(header.number) })}</h1>
        <StatusBadge value={String(header.state)} />
      </div>

      <div className="card">
        <dl className="kv">
          <dt>{t('shipments.colCarrier')}</dt>
          <dd>{header.carrier_kind ? String(header.carrier_kind) : '—'}</dd>
          <dt>{t('shipmentDetail.carrierRef')}</dt>
          <dd>{header.carrier_ref ? String(header.carrier_ref) : '—'}</dd>
          <dt>{t('shipmentDetail.departed')}</dt>
          <dd>{header.departed_at ? formatDateTime(String(header.departed_at), locale) : t('shipmentDetail.notYetDeparted')}</dd>
        </dl>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('shipmentDetail.packagesCount', { n: packages.length })}</h2>
        {packages.length === 0 ? (
          <p>{t('shipmentDetail.noPackages')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('shipmentDetail.colCode')}</th>
                <th>{t('shipmentDetail.colWeight')}</th>
                <th>{t('shipmentDetail.colContents')}</th>
                <th>{t('shipmentDetail.colLabel')}</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((row, i) => {
                const r = row as Record<string, unknown>;
                const packageContents = contents.filter((c) => (c as Record<string, unknown>).package_id === r.id);
                return (
                  <tr key={i}>
                    <td>{String(r.code)}</td>
                    <td>{r.weight_grams ? formatNumber(String(r.weight_grams), locale) : '—'}</td>
                    <td>
                      {packageContents.map((c, j) => {
                        const cc = c as Record<string, unknown>;
                        return (
                          <div key={j}>
                            {String(cc.sku_code)} × {formatNumber(String(cc.qty), locale)}
                          </div>
                        );
                      })}
                    </td>
                    <td>
                      <PackageLabelCell packageId={String(r.id)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('shipmentDetail.custodyExit')}</h2>
        {exitMovements.length === 0 ? (
          <p>{t('shipmentDetail.custodyNotExited')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventory.colSku')}</th>
                <th>{t('inboundDetail.colQty')}</th>
                <th>{t('inventoryDetail.colOccurred')}</th>
              </tr>
            </thead>
            <tbody>
              {exitMovements.map((row, i) => {
                const r = row as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>{String(r.sku_code)}</td>
                    <td>{formatNumber(String(r.qty), locale)}</td>
                    <td>{formatDateTime(String(r.occurred_at), locale)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
